import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommercialClaimInfo } from 'app/commercial/models/claim-info.model';
import { hasOwnPropertyDescriptor } from 'app/commercial/models/object-property.util';
import { environment } from 'environments/environment';
import { Subscription } from 'rxjs';
import { commercialConstants } from '../../../../../common/commercial-constants';
import { LoggerService } from '../../../../../shared/services/logger.service';
import { ClaimsDataService } from '../../../services/claims-data.service';
import { CommercialLogMetadata, CommercialLoggingService } from '../../../services/commercial-logging.service';

import { BreadCrumb } from '@nationwide/internet-servicing-angular-components';

@Component({
    selector: 'is-incident-details',
    templateUrl: './incident-details.component.html',
    styleUrls: ['./incident-details.component.scss'],
    standalone: false
})
export class IncidentDetailsComponent implements OnInit, OnDestroy {
    breadCrumbs: BreadCrumb[] = [];
    claimInfo: CommercialClaimInfo | null = null;
    currentPage = 'Incident Details';
    displayName = false;
    hasPremisis = false;
    homeIconLink = environment.NATIONWIDE.accountSummaryPage;
    incidentOnly = false;
    isWC = true;
    jurisdictionState = false;
    lossCause = false;
    lossDate = false;
    lossLocation = false;
    policeNotified = false;
    relationToInsured = false;
    reportedDate = false;
    serviceFlag = false;
    subscription = new Subscription();
    toolTipVars = { claimDetailsHelptext1: false };

    constructor(
        public claimsDataService: ClaimsDataService,
        private LOGGER: LoggerService,
        private commercialLoggingService: CommercialLoggingService
    ) { }

    getClaimInfo(): void {
        this.claimInfo = this.claimsDataService.getClaimInformation() as CommercialClaimInfo;
        if (this.claimInfo) {
            this.serviceFlag = true;
            this.lossCause = hasOwnPropertyDescriptor(this.claimInfo, 'lossCause');
            this.incidentOnly = !!this.claimInfo.incidentOnly;
            this.reportedDate = hasOwnPropertyDescriptor(this.claimInfo, 'reportedDate');
            this.displayName = hasOwnPropertyDescriptor(this.claimInfo?.claimReporter?.reportedBy, 'displayName');
            this.relationToInsured = hasOwnPropertyDescriptor(this.claimInfo?.claimReporter, 'relationToInsured');
            this.lossDate = hasOwnPropertyDescriptor(this.claimInfo, 'lossDate');
            this.lossLocation = hasOwnPropertyDescriptor(this.claimInfo, 'lossLocation');
            this.jurisdictionState = hasOwnPropertyDescriptor(this.claimInfo, 'jurisdictionState');
            this.policeNotified = hasOwnPropertyDescriptor(this.claimInfo, 'policeNotified');
            if (this.claimsDataService.isFromSearch()) {
                this.hasPremisis = true;
                this.claimsDataService.retrievePremisisID(this.claimInfo);
            } else {
                const premisesId = this.claimsDataService.getPremisisID();
                if (premisesId !== undefined && premisesId !== null) {
                    this.hasPremisis = true;
                }
                this.claimsDataService.setIsSearching(false);
            }
        }
    }

    getLocation(location: Record<string, unknown>): string {
        return this.claimsDataService.formatLossLocationDisplay(location);
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    ngOnInit(): void {
        this.breadCrumbs = [
            {
                name: 'Package (PAK) Claims',
                link: `${commercialConstants.URLS.pakClaimsList}`
            }, {
                name: 'Claim Overview',
                link: `${commercialConstants.URLS.claimOverview}`
            }
        ];
        this.getClaimInfo();
        this.commercialLoggingService.logPageLoad(this.LOGGER, this.currentPage, this.getLogMetadata());
        this.isWC = this.claimsDataService.getFnolFlow() !== 'Workers Compensation';
        this.commercialLoggingService.updatePreviousUrlContext();
    }

    private getLogMetadata(additionalFields: Partial<CommercialLogMetadata> = {}): Partial<CommercialLogMetadata> {
        return {
            policyNumber: this.claimsDataService.getClaimsPolicyNumber(),
            policyType: this.claimsDataService.getClaimsPolicyType(),
            lineOfBusiness: this.claimsDataService.getFnolFlow(),
            claimsDisplayCount: this.claimsDataService.getClaimsDisplayCount() || 'UNKNOWN',
            alphaState: this.claimsDataService.getPrimary(this.claimsDataService.getClaimsPackage()?.address)?.state || 'UNKNOWN',
            previousUrl: window.sessionStorage.getItem('previousUrl') || '',
            claimNumber: this.claimInfo?.claimNumber || '',
            claimStatus: this.claimInfo?.claimState || '',
            incidentDate: this.claimInfo?.lossDate || '',
            lossCause: this.claimInfo?.lossCause || '',
            ...additionalFields
        };
    }
}
